import clsx from 'clsx';
import { format, isValid, parse } from 'date-fns';
import React from 'react';
import {
  Control,
  FieldError,
  FieldPath,
  FieldPathValue,
  FieldValues,
  get,
  useController,
} from 'react-hook-form';
import FormError from '../FormError';
import FormLabel from '../FormLabel';
import TextInput from '../TextInput';

interface Props {
  className?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  value?: string;
  name?: string;
  autoComplete?: 'bday' | 'cc-exp';
}

interface DateValue {
  year: string;
  month: string;
  day: string;
}

const DEFAULT_DATE = { year: '', month: '', day: '' };

function parseDate(value?: string): DateValue {
  if (!value) return DEFAULT_DATE;
  const result = value.split('-');
  return {
    year: result[0] || '',
    month: result[1] || '',
    day: result[2] || '',
  };
}

function tryFormatDate(dateStr: string): string {
  try {
    const result = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(result) ? format(result, 'yyyy-MM-dd') : dateStr;
  } catch {
    return dateStr;
  }
}

function cleanValue(val: string): string {
  return val.replace(/[^0-9]/g, '');
}

const DateInput = function DateInput({
  className,
  disabled,
  onChange,
  onBlur,
  value,
  name,
  autoComplete,
}: Props): JSX.Element {
  const dateValue = parseDate(value);

  const onYearChange = (val: string): void => {
    if (!onChange) return;
    const cleanedVal = cleanValue(val);
    onChange(`${cleanedVal}-${dateValue.month}-${dateValue.day}`);
  };
  const onMonthChange = (val: string): void => {
    if (!onChange) return;
    const cleanedVal = cleanValue(val);
    onChange(`${dateValue.year}-${cleanedVal}-${dateValue.day}`);
  };
  const onDayChange = (val: string): void => {
    if (!onChange) return;
    const cleanedVal = cleanValue(val);
    onChange(`${dateValue.year}-${dateValue.month}-${cleanedVal}`);
  };
  const handleBlur = (): void => {
    if (value && onChange) {
      const formattedDate = tryFormatDate(value);
      if (formattedDate !== value) {
        onChange(formattedDate);
      }
    }
    if (onBlur) onBlur();
  };
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    e.target.select();
  };
  return (
    <div className={clsx('flex space-x-2', className)}>
      <TextInput
        onChange={onYearChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        className="grow-[2] basis-0"
        value={dateValue.year}
        placeholder="YYYY"
        pattern="[0-9]+"
        maxLength={4}
        name={name && `${name}-year`}
        autoComplete={autoComplete && `${autoComplete}-year`}
      />
      <TextInput
        onChange={onMonthChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        className="flex-1"
        value={dateValue.month}
        placeholder="MM"
        pattern="[0-9]+"
        maxLength={2}
        name={name && `${name}-month`}
        autoComplete={autoComplete && `${autoComplete}-month`}
      />
      <TextInput
        onChange={onDayChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        className="flex-1"
        value={dateValue.day}
        placeholder="DD"
        pattern="[0-9]+"
        maxLength={2}
        name={name && `${name}-day`}
        autoComplete={autoComplete && `${autoComplete}-day`}
      />
    </div>
  );
};

interface DateInputLabelledProps extends Props {
  label?: string;
  error?: React.ReactNode;
}

DateInput.Labelled = function TextInputLabelled({
  label,
  className,
  error,
  ...rest
}: DateInputLabelledProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <div className={className}>
      {label && <FormLabel>{label}</FormLabel>}
      <DateInput {...rest} />
      {error && <FormError>{error}</FormError>}
    </div>
  );
};

interface DateInputControllerProps<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends DateInputLabelledProps {
  control: Control<TFieldValues>;
  name: TFieldName;
}

DateInput.LabelledController = function DateInputController<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  ...rest
}: DateInputControllerProps<TFieldValues, TFieldName>): JSX.Element {
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name });
  const error = get(errors, name) as FieldError | undefined;

  return (
    <DateInput.Labelled
      onChange={(val) =>
        onChange(val as FieldPathValue<TFieldValues, TFieldName>)
      }
      value={value as string}
      error={error?.message}
      name={name}
      {...rest}
    />
  );
};

export default DateInput;
